"use client";

import { Button } from "@/components/ui/button";
import Image from "next/image";
import Link from "next/link";
import { MobileMenu } from "./MobileMenu";
import { navigation as defaultNavigation, navigationLetsTalk as defaultLetsTalk } from "./navigation-config";
import { fontWeights } from "@/utils/fonts";
import { Ticker } from "../home/ticker";
import { useState } from "react";

interface NavLink {
  name: string;
  href: string;
  show?: boolean;
}

interface LetsTalkLink {
  name: string;
  href: string;
  show?: boolean;
  buttonBgColor?: string;
  buttonTextColor?: string;
  buttonHoverBgColor?: string;
}

interface HeaderData {
  logo?: string;
  backgroundColor?: string;
  navLinks?: NavLink[];
  letsTalk?: LetsTalkLink;
  tickerMessages?: Array<{ text: string; show?: boolean }>;
  tickerBackgroundColor?: string;
  tickerTextColor?: string;
  navTextColor?: string;
  navHoverColor?: string;
}

interface NavbarProps {
  initialNavStatus?: Record<string, boolean>;
  headerData?: HeaderData;
}

export function Navbar({ initialNavStatus, headerData }: NavbarProps) {
  const [navStatus] = useState<Record<string, boolean>>(initialNavStatus || {});

  // Use CMS nav links if available, otherwise fall back to static config
  const cmsNavLinks = headerData?.navLinks?.filter(l => l.show !== false) ?? null;
  const cmsLetsTalk = headerData?.letsTalk;
  const cmsLogo = headerData?.logo;

  const navLinks: NavLink[] = cmsNavLinks ?? [...defaultNavigation];
  const letsTalkLinks: LetsTalkLink[] = cmsLetsTalk
    ? (cmsLetsTalk.show !== false ? [cmsLetsTalk] : [])
    : [...defaultLetsTalk as any];

  // Filter navigation based on CMS page status
  const visibleNavigation = navLinks.filter(item => {
    if (item.href === '/') return true;
    const slug = item.href.replace(/^\//, '').toLowerCase();
    return navStatus[slug] !== false;
  });

  return (
    <nav className="sticky top-0 z-50">
      <div
        className="py-[8px] 3xl:py-[10px]"
        style={{ backgroundColor: headerData?.backgroundColor || '#002F6D' }}
      >
        <div
          className="
        max-w-[1920px] 
        mx-auto 
        px-4 
        sm:px-6 
        lg:px-[50px] 
        xl:px-[100px] 
        2xl:px-[200px] 
        3xl:px-[300px]
        "
        >
          <div className="flex justify-between items-center lato-font">
            <div className="flex-shrink-0">
              <Link href="/" aria-label="BitsShadow Home">
                <Image
                  src={cmsLogo || "/bitsshadow-logo.svg"}
                  alt="BitsShadow"
                  width={242}
                  height={80}
                  className="
                w-[100px]
                tsm-350:w-[151px]
                sm:w-[182px]
                md:w-[162px]
                lg:w-[212px]
                3xl:w-[242px]
                h-[35px]
                tsm-350:h-[50px]
                sm:h-[60px]
                md:h-[60px]
                lg:h-[70px] 
                3xl:h-[80px]
                "
                  priority
                />
              </Link>
            </div>

            <div className="hidden md:flex items-center gap-8 md:gap-4 lg:gap-8 2xl:gap-14" style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.medium }}>
              <div className="flex items-center md:gap-3 lg:gap-7 2xl:gap-10">
                {visibleNavigation.map((item) => (
                  <Link
                    key={item.name}
                    href={item.href}
                    className="transition-colors sm:text-sm lg:text-base whitespace-nowrap"
                    style={{ 
                      color: headerData?.navTextColor || '#ffffff'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = headerData?.navHoverColor || '#FF9202';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = headerData?.navTextColor || '#ffffff';
                    }}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>

              <div className="pl-4 md:pl-0 lg:pl-6 2xl:pl-6">
                {letsTalkLinks.map((item) => (
                  <Button
                    key={item.name}
                    variant="outline"
                    className="
                   rounded-[40px] 
                   lg:rounded-[30px] 
                   px-7 
                   lg:px-[39px] 
                   py-[1px] 
                   lg:py-[11px] 
                   h-[32px] 
                   lg:h-[40px] 
                   min-w-[110px] 
                   lg:min-w-[138px]
                   sm:text-sm 
                   lg:text-base 
                   xl:text-base 
                   2xl:text-lg 
                   lg:mr-3
                   mr-4
                   whitespace-nowrap
                   border-0
                   transition-all
                   duration-300
                   "
                    style={{
                      backgroundColor: item.buttonBgColor || '#FF9202',
                      color: item.buttonTextColor || '#ffffff'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = item.buttonHoverBgColor || '#e68302';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = item.buttonBgColor || '#FF9202';
                    }}
                  >
                    <Link href={item.href} target="_blank" rel="noopener noreferrer">{item.name}</Link>
                  </Button>
                ))}
              </div>
            </div>
            <MobileMenu 
              navStatus={navStatus} 
              navLinks={visibleNavigation} 
              letsTalkLinks={letsTalkLinks} 
              navTextColor={headerData?.navTextColor}
              navHoverColor={headerData?.navHoverColor}
            />
          </div>
        </div>
      </div>
      <Ticker
        messages={headerData?.tickerMessages}
        backgroundColor={headerData?.tickerBackgroundColor}
        textColor={headerData?.tickerTextColor}
      />
    </nav>
  );
}
